/**
 * `withOpenRecords(zodSchema)` — build a tool `inputSchema` whose open,
 * arbitrary-key objects (`z.record(...)` maps) survive AI SDK 7's schema
 * conversion.
 *
 * Why this exists: AI SDK's zod→JSON-Schema converter (`asSchema`) forces
 * `additionalProperties: false` on *every* object for strict function-calling —
 * including `z.record(...)` maps, which declare no fixed `properties` and rely
 * on `additionalProperties` to admit their entries. Under the workflow runtime
 * `@ai-sdk/workflow` compiles the serialized schema with Ajv, so a closed record
 * rejects *every* key with "must NOT have additional properties", breaking any
 * tool that takes an arbitrary-key object (batch-op `args`, key/value
 * `metadata`, the progressive-disclosure proxy's `toolInputs`). See ADR-075.
 *
 * This converts the schema, then re-opens only the record-shaped nodes — an
 * object with `additionalProperties: false` and no declared `properties`, which
 * post-conversion accepts nothing and is therefore never what the author meant.
 * Fixed-shape objects stay strict. Type inference is preserved via the
 * `z.ZodType<T>` generic, so the result drops into a tool's `inputSchema`
 * unchanged.
 *
 * Limitation: only pure `z.record(...)` maps are detected. A `.catchall(...)` /
 * passthrough object carries `properties: {}` and is indistinguishable from an
 * empty strict object after conversion — hand-author those as `jsonSchema`.
 */

// Import the schema helpers from the low-level `@ai-sdk/provider-utils` /
// `@ai-sdk/provider` packages, NOT the `ai` barrel. This module is reachable
// from the WDK *workflow bundle* (via `progressive-disclosure.ts`), and the
// `ai` barrel drags in a Node.js builtin through its `asSchema` export, which
// the workflow bundler rejects ("Packages that depend on Node.js modules are
// not available in workflow functions"). `provider-utils` is the runtime-
// agnostic package where `asSchema`/`jsonSchema` are actually defined and is
// free of Node builtins. See the bundle-constraints banner in
// `apps/web/workflows/chat/workflow.ts`.
import type { JSONSchema7 } from "@ai-sdk/provider";
import { asSchema, jsonSchema, type Schema } from "@ai-sdk/provider-utils";
import type { z } from "zod";

export function withOpenRecords<T>(schema: z.ZodType<T>): Schema<T> {
  const json = structuredClone(asSchema(schema).jsonSchema) as JSONSchema7;
  reopenRecordObjects(json);
  return jsonSchema<T>(json);
}

function reopenRecordObjects(node: unknown): void {
  if (Array.isArray(node)) {
    for (const child of node) {
      reopenRecordObjects(child);
    }
    return;
  }
  if (node === null || typeof node !== "object") {
    return;
  }
  const obj = node as Record<string, unknown>;

  if (
    obj.type === "object" &&
    obj.additionalProperties === false &&
    !("properties" in obj)
  ) {
    obj.additionalProperties = true;
  }

  for (const key of [
    "properties",
    "$defs",
    "definitions",
    "patternProperties",
  ] as const) {
    const container = obj[key];
    if (container && typeof container === "object") {
      for (const child of Object.values(container as Record<string, unknown>)) {
        reopenRecordObjects(child);
      }
    }
  }
  for (const key of [
    "items",
    "additionalProperties",
    "not",
    "if",
    "then",
    "else",
  ] as const) {
    const child = obj[key];
    if (child && typeof child === "object") {
      reopenRecordObjects(child);
    }
  }
  for (const key of ["anyOf", "allOf", "oneOf", "prefixItems"] as const) {
    const arr = obj[key];
    if (Array.isArray(arr)) {
      for (const child of arr) {
        reopenRecordObjects(child);
      }
    }
  }
}
